import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Check, AlertCircle, Mail, CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

export default function StaffInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, sendMagicLink, loading: authLoading } = useAuth();
  const functions = getFunctions();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth form state
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Acceptance state
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Validate token on load
  useEffect(() => {
    const validate = async () => {
      try {
        const validateFn = httpsCallable(functions, 'validateStaffInvite');
        const result = await validateFn({ token });

        if (result.data.success) {
          setInvite(result.data.invite);
          setEmail(result.data.invite.email || '');
        }
      } catch (err) {
        setError(err.message || 'This invitation is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [token, functions]);

  // If user is already logged in, accept immediately
  useEffect(() => {
    if (user && invite && !accepted && !accepting) {
      acceptInvite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, invite]);

  const acceptInvite = async () => {
    setAccepting(true);
    setAuthError(null);

    try {
      const acceptFn = httpsCallable(functions, 'acceptStaffInvite');
      const result = await acceptFn({ token });

      if (result.data.success) {
        setAccepted(true);
        // Redirect to community dashboard after a brief moment
        setTimeout(() => {
          navigate(`/community/${result.data.communityId}`);
        }, 2000);
      }
    } catch (err) {
      setAuthError(err.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setAuthError('Please enter your email address');
      return;
    }
    setSubmitting(true);
    setAuthError(null);

    try {
      // After sign-in completes, land back on this invite page so the
      // auto-accept effect above runs with the invite token.
      await sendMagicLink(email.trim(), { continuePath: `/staff-invite/${token}` });
      setLinkSent(true);
    } catch (err) {
      let message = 'Could not send the sign-in link. Please try again.';
      if (err.code === 'auth/invalid-email') message = 'Please enter a valid email address';
      else if (err.code === 'auth/too-many-requests') message = 'Too many attempts. Try again shortly.';
      setAuthError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-quiltt-purple hover:underline"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome to the team!</h2>
          <p className="text-gray-600">
            You've joined <strong>{invite.communityName}</strong>.
          </p>
        </div>
      </div>
    );
  }

  // Logged in but mid-accept (or accept failed)
  if (user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          {authError ? (
            <>
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">Couldn't join</h2>
              <p className="text-gray-500 mb-6">{authError}</p>
              <button
                onClick={acceptInvite}
                className="text-quiltt-purple hover:underline font-medium"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <div className="w-10 h-10 border-3 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Joining {invite.communityName}...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Not logged in — send magic link
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-quiltt-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-quiltt-purple" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Join {invite?.communityName}
              </h1>
              <p className="text-sm text-gray-500">
                You've been invited to join the care team.
              </p>
            </div>
          </div>

          {linkSent ? (
            <div className="text-center py-2">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Check your email</h2>
              <p className="text-gray-600 mb-1">We sent a sign-in link to</p>
              <p className="text-gray-900 font-medium mb-6 break-all">{email}</p>
              <p className="text-sm text-gray-500">
                Open it on this device and tap the link. You'll be brought right back
                here to finish joining the team.
              </p>
            </div>
          ) : (
            <>
              {authError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{authError}</p>
                </div>
              )}

              <form onSubmit={handleSendLink}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent outline-none mb-4"
                />
                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send sign-in link
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
